import React, { useState, useEffect } from 'react';

interface CountdownTimerProps {
  targetDate: string | null;
}

export interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export function calculateTimeRemaining(targetDate: string, now: Date = new Date()): TimeRemaining | null {
  const target = new Date(targetDate);
  const diffMs = target.getTime() - now.getTime();

  if (diffMs <= 0) {
    return null;
  }

  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds };
}

const styles: Record<string, React.CSSProperties> = {
  banner: {
    width: '100%',
    padding: '1.5rem',
    backgroundColor: '#1a6b3c',
    borderRadius: '8px',
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    marginBottom: '1.25rem',
  },
  timeContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1.5rem',
    flexWrap: 'wrap',
  },
  timeBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  timeValue: {
    fontSize: '2.5rem',
    fontWeight: 700,
    color: '#fff',
    lineHeight: 1.2,
  },
  timeLabel: {
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.85)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: '0.25rem',
  },
  message: {
    fontSize: '1.1rem',
    color: '#fff',
    fontWeight: 600,
  },
  subtitle: {
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.85)',
    marginTop: '0.5rem',
  },
};

export function CountdownTimer({ targetDate }: CountdownTimerProps) {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(null);
  const [isHappeningNow, setIsHappeningNow] = useState(false);

  useEffect(() => {
    if (!targetDate) return;

    function tick() {
      const remaining = calculateTimeRemaining(targetDate!);
      if (remaining === null) {
        setTimeRemaining(null);
        setIsHappeningNow(true);
      } else {
        setTimeRemaining(remaining);
        setIsHappeningNow(false);
      }
    }

    tick();
    const intervalId = setInterval(tick, 1000);

    return () => clearInterval(intervalId);
  }, [targetDate]);

  if (!targetDate) {
    return (
      <div style={styles.banner}>
        <div style={styles.message}>No upcoming events.</div>
      </div>
    );
  }

  if (isHappeningNow) {
    return (
      <div style={styles.banner}>
        <div style={styles.message}>Happening now!</div>
      </div>
    );
  }

  if (!timeRemaining) {
    return null;
  }

  return (
    <div style={styles.banner}>
      <div style={styles.timeContainer}>
        <div style={styles.timeBlock}>
          <span style={styles.timeValue}>{timeRemaining.days}</span>
          <span style={styles.timeLabel}>{timeRemaining.days === 1 ? 'Day' : 'Days'}</span>
        </div>
        <div style={styles.timeBlock}>
          <span style={styles.timeValue}>{timeRemaining.hours}</span>
          <span style={styles.timeLabel}>{timeRemaining.hours === 1 ? 'Hour' : 'Hours'}</span>
        </div>
        <div style={styles.timeBlock}>
          <span style={styles.timeValue}>{timeRemaining.minutes}</span>
          <span style={styles.timeLabel}>{timeRemaining.minutes === 1 ? 'Minute' : 'Minutes'}</span>
        </div>
        <div style={styles.timeBlock}>
          <span style={styles.timeValue}>{timeRemaining.seconds}</span>
          <span style={styles.timeLabel}>{timeRemaining.seconds === 1 ? 'Second' : 'Seconds'}</span>
        </div>
      </div>
      <div style={styles.subtitle}>until your next event</div>
    </div>
  );
}
